import React, { useState } from "react";
import { X, Save, Shield } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ROLE_TONES, DEFAULT_PERMISSION_LABELS, DEFAULT_TAB_LABELS, ROLE_LABELS, ALL_TABS, ROLE_TAB_SCOPE } from "./userUtils";

export default function PermissionsModal({ perms, labels, onClose, onSaved }) {
  const [draft, setDraft] = useState(() => {
    const { _labels, ...clean } = perms || {};
    return JSON.parse(JSON.stringify(clean));
  });
  const [saving, setSaving] = useState(false);

  const permLabels = labels?.permissions || DEFAULT_PERMISSION_LABELS;
  const tabLabels = labels?.tabs || DEFAULT_TAB_LABELS;
  const roleLabels = labels?.roles || ROLE_LABELS;

  const toggleTab = (role, tab) => {
    const current = draft[role]?.tabs || [];
    const next = current.includes(tab) ? current.filter((t) => t !== tab) : [...current, tab];
    setDraft({ ...draft, [role]: { ...draft[role], tabs: next } });
  };
  const togglePerm = (role, key) => {
    setDraft({ ...draft, [role]: { ...draft[role], [key]: !draft[role]?.[key] } });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { yonetici: draft.yonetici, muhasebeci: draft.muhasebeci, muhasebe_stj: draft.muhasebe_stj, junior_muhasebe: draft.junior_muhasebe, personel: draft.personel };
      await api.put("/role-permissions", payload);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const roles = ["yonetici", "muhasebeci", "muhasebe_stj", "junior_muhasebe", "personel"];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-3xl my-6" data-testid="perms-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Shield size={18} /> Rol İzinleri
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
            Her rolün hangi sekmelere erişebileceğini ve para rakamlarını görüp göremeyeceğini buradan ayarlayın. Admin rolü her zaman tüm izinlere sahiptir. Her rol için yalnızca o rolün veri erişimine uygun sekmeler listelenir. Değişiklikler, kullanıcıların bir sonraki girişinde geçerli olur.
          </div>
          {roles.map((role) => (
            <div key={role} className="border border-slate-200 rounded-xl p-4" data-testid={`perms-role-${role}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${ROLE_TONES[role]}`}>
                  {roleLabels[role] || role}
                </span>
              </div>

              <div className="mb-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Erişilebilir Sekmeler</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {ALL_TABS.filter((tab) => (ROLE_TAB_SCOPE[role] || ALL_TABS).includes(tab)).map((tab) => {
                    const checked = (draft[role]?.tabs || []).includes(tab);
                    return (
                      <label key={tab} className={`flex items-center gap-2 text-xs cursor-pointer rounded-lg px-3 py-2 border ${checked ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}>
                        <input
                          data-testid={`perm-tab-${role}-${tab}`}
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleTab(role, tab)}
                        />
                        <span className="font-semibold">{tabLabels[tab] || tab}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">İzinler</div>
                <div className="space-y-2">
                  {Object.entries(permLabels).map(([k, label]) => (
                    <label key={k} className="flex items-start gap-2 text-xs cursor-pointer">
                      <input
                        data-testid={`perm-${role}-${k}`}
                        type="checkbox"
                        checked={!!draft[role]?.[k]}
                        onChange={() => togglePerm(role, k)}
                        className="mt-0.5"
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {draft.personel && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <strong>Personel notu:</strong> Personel rolü Prim ekranında yalnızca tarih, icra föy, icra esas, dosya adı, müvekkil adı, varlık, vekalet ücreti, tahsilat miktarı ve giden kişi alanlarını görür/düzenler. Para hesaplamaları (oran, ödenecek) her durumda gizlidir.
            </div>
          )}
        </div>
        <div className="p-5 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50">İptal</button>
          <button
            data-testid="perms-save-button"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}
